import { AuthSection } from 'components/templates/templates-wizard/vcs/common/auth-section';
import React, { useCallback, useEffect, useMemo } from 'react';
import type { SubFormProps } from 'components/common/form-controlled/with-controller.hoc';
import { FieldRow } from 'components/common/form-components';
import ControlledTextInput from 'components/common/form-controlled/controlled-text-input';
import type { GitProviderTypes } from 'types/api.types';
import type { BlueprintApi } from '@env0/blueprint-service/api';
import {
  providersSupportingVcsRepositoryIntegration,
  providerSupportingOauthIntegration
} from 'components/templates/templates-wizard/vcs/use-git-provider.hook';
import { observer } from 'mobx-react';
import Loader from 'components/common/loader';
import type { VCSForm } from 'components/templates/templates-wizard/common/template-wizard.types';
import { LocationSection } from 'components/templates/templates-wizard/vcs/common/location-section';
import Divider from 'components/common/divider';
import { css } from '@emotion/react';
import styled from 'types/theme.types';
import useAuthTokens from './use-auth-tokens.hook';
import { useIntl } from 'react-intl';
import type { VcsApi } from '@env0/vcs-service/api';
import VcsRepositoryOauthSelector from 'components/templates/templates-wizard/vcs/vcs-repository-oauth-selector';
import VcsRepositorySelfHostedSelector from 'components/templates/templates-wizard/vcs/vcs-repository-self-hosted-selector';
import VcsConnectionSelectField from 'components/templates/templates-wizard/vcs/vcs-connection-select-field';

export type TemplateWizardStepVCSFieldsProps = SubFormProps<VCSForm> & {
  gitProvider: GitProviderTypes;
  isLoadingRepositories: boolean;
  repositories: BlueprintApi.GetAvailableRepositories.Response.Body;
  templateType: BlueprintApi.BlueprintType;
  isNewTemplate?: boolean;
  vcsRepositoryDisabled?: boolean;
  vcsRevisionDisabled?: boolean;
  showLocationSection?: boolean;
};

const TemplateWizardStepVCSFields: React.FunctionComponent<TemplateWizardStepVCSFieldsProps> = ({
  form,
  gitProvider,
  repositories,
  templateType: rawTemplateType,
  isNewTemplate,
  vcsRepositoryDisabled,
  vcsRevisionDisabled,
  showLocationSection = true
}) => {
  const { setValue, trigger, clearErrors } = form;
  const intl = useIntl();
  const templateType = intl.formatMessage({ id: rawTemplateType || 'opentofu' });
  const selectRepositoryFromVcsDropdown = providersSupportingVcsRepositoryIntegration.has(gitProvider);
  const showRevisionsDropdown = ['GitHub', 'GitLab', 'BitBucket', 'AzureDevOps'].includes(gitProvider);
  const showOauthSelector = providerSupportingOauthIntegration.has(gitProvider);
  const repoMessageId = useMemo(() => `templates.add.new.field.repository.${gitProvider.toLowerCase()}`, [gitProvider]);

  const { repository } = form.watch();

  const { tokens, isLoadingTokens } = useAuthTokens(form, gitProvider);

  const initVSCData = useCallback(
    (newRepository: string) => {
      const currentRepository = repositories.find(repo => repo.httpsUrl === newRepository);
      if (!currentRepository) return;

      if (gitProvider === 'BitBucket') {
        setValue('bitbucketClientKey', currentRepository.bitbucket?.clientKey || null);
      }
    },
    [gitProvider, repositories, setValue]
  );

  // init VSC data from repositories - Part of them required for update
  useEffect(() => {
    if (!isNewTemplate) {
      initVSCData(repository);
    }
  }, [isNewTemplate, initVSCData, repository]);

  useEffect(() => {
    if (rawTemplateType !== 'cloudformation') {
      setValue('fileName', '');
    }
    trigger('fileName').then(() => {
      clearErrors('fileName');
    });
  }, [setValue, trigger, clearErrors, rawTemplateType]);

  const repositoryLabelProps = {
    inline: true,
    width: 50,
    label: { id: repoMessageId },
    mandatory: true,
    info: { id: `${repoMessageId}.info`, values: { templateType } }
  };

  if (isLoadingTokens) {
    return <Loader />;
  }

  const repositoryTooltip = vcsRepositoryDisabled
    ? { placement: 'top', titleId: 'templates.add.new.field.repository.disabled' }
    : undefined;

  const renderRepositoryField = () => {
    if (selectRepositoryFromVcsDropdown) {
      const VcsSelector = showOauthSelector ? VcsRepositoryOauthSelector : VcsRepositorySelfHostedSelector;

      return <VcsSelector {...repositoryLabelProps} vcsType={gitProvider as VcsApi.VcsType} form={form} />;
    }

    return (
      <ControlledTextInput
        {...repositoryLabelProps}
        form={form}
        id="add-new-template-repository-input"
        name="repository"
        data-e2e="repository-url-field"
        placeholder={`templates.add.new.field.repository.${
          gitProvider === 'HelmRepository' ? 'helmRepo' : 'other'
        }.placeholder`}
        disabled={vcsRepositoryDisabled}
        tooltip={repositoryTooltip}
      />
    );
  };

  return (
    <Container>
      <FieldRow zeroBottomMargin>{renderRepositoryField()}</FieldRow>
      {selectRepositoryFromVcsDropdown && (
        <VcsConnectionSelectField form={form} vcsType={gitProvider as VcsApi.VcsType} />
      )}
      {showLocationSection && (
        <LocationSection
          form={form}
          rawTemplateType={rawTemplateType}
          gitProvider={gitProvider}
          vcsRevisionDisabled={vcsRevisionDisabled}
          showRevisionsDropdown={showRevisionsDropdown}
          templateType={templateType as BlueprintApi.BlueprintType}
        />
      )}
      {rawTemplateType === 'module' && (
        <div data-e2e={`module-tag-prefix-section`}>
          <Divider data-e2e={`module-tag-prefix-divider`} titleId="templates.add.new.divider.tag-prefix" />
          <FieldRow>
            {
              <ControlledTextInput
                inline={true}
                width={50}
                label={{ id: 'templates.add.new.field.repository.tag-prefix' }}
                info={{ id: 'templates.add.new.field.repository.tag-prefix.info' }}
                form={form}
                id="add-new-template-repository-tag-prefix-input"
                name="tagPrefix"
                data-e2e="repository-tag-prefix-field"
              />
            }
          </FieldRow>
        </div>
      )}
      {gitProvider === 'Other' && rawTemplateType !== 'helm' && (
        <AuthSection form={form} gitProvider={gitProvider} tokens={tokens!} />
      )}
    </Container>
  );
};

const Container = styled.div`
  ${({ theme }) => css`
    margin-block-start: ${theme.spacing.lg};
  `}
`;

export default observer(TemplateWizardStepVCSFields);
